<template>
  <v-container class="patient-information-form-wrapper">
    <div v-if="preloader">
      <SubmissionPageSkeleton />
    </div>
    <div v-if="reopeningStatuses.includes(orderInfo.status)" class="mb-4">
      <v-chip :color="isReopenEligible ? 'success' : 'warning'" variant="tonal" size="large" block class="pa-4">
        <template v-if="isReopenEligible"> ✓ Ready to re-open submission </template>
        <template v-else>
          <span v-if="orderInfo.status === 'Response received'">
            ⚠ In order to re-open a submission you need to update one or more of the following sections: Patient,
            Provider or Prescription Information.
          </span>
          <span v-if="orderInfo.status === 'Payor/Medication not supported'">
            ⚠ In order to re-open a submission you need to update either of the following fields: RxBIN, RxPCN, RxGroup,
            National Drug Code or Medication.
          </span>
        </template>
      </v-chip>
    </div>

    <v-expansion-panels v-if="!preloader" v-model="expandedSections" multiple>
      <v-expansion-panel
        v-if="
          orderInfo &&
          orderInfo.portalForms &&
          orderInfo.portalForms?.length &&
          orderInfo.submissionMethod === 'CoverMyMeds'
        "
        value="portal-forms">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-checkbox-marked" class="mr-2"></v-icon>
          {{ orderInfo.portalSubmissionForm }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <PortalFormSelection v-if="!preloader" :order-info="orderInfo" @change-form-selection="changeFormSelection" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel value="patient-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-account" class="mr-2"></v-icon>
          Patient Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <PatientInformationFormPreview
            v-if="!preloader"
            :order-info="orderInfo"
            :is-editable="!props.disabled && (incompleteStatuses.includes(orderInfo.status) || isReopening)"
            :form-button-clicked="formButtonClicked"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Provider Information Section -->
      <v-expansion-panel value="provider-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-doctor" class="mr-2"></v-icon>
          Provider Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ProviderInformationFormPreview
            v-if="!preloader"
            :order-info="orderInfo"
            :is-editable="!props.disabled && (incompleteStatuses.includes(orderInfo.status) || isReopening)"
            :form-button-clicked="formButtonClicked"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Medical Information Section -->
      <v-expansion-panel value="medical-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-medication" class="mr-2"></v-icon>
          Prescription Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <MedicalInformationForm
            v-if="!preloader"
            :order-info="orderInfo"
            :is-editable="!props.disabled && (incompleteStatuses.includes(orderInfo.status) || isReopening)"
            :form-button-clicked="formButtonClicked"
            :medication-search-box="medicationSearchBox"
            @update-order-info-field="updateOrderInfoField"
            @add-extra-medication="addExtraMedication"
            @remove-extra-medication="removeExtraMedication"
            @remove-primary-medication="removePrimaryMedication"
            @update-extra-medication="updateExtraMedicationField"
            @change-formulary="changeFormulary" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Patient Diagnosis Information Section -->
      <v-expansion-panel value="diagnosis-info">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-account-heart" class="mr-2"></v-icon>
          Patient Diagnosis Information
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row class="patient-information-container bg-snow rounded-16">
            <v-col cols="12">
              <v-row class="patient-information">
                <v-col cols="12" class="header-with-select-box">
                  <DiagnosisSelectionPreviewComponent
                    :medication="orderInfo.medication"
                    :order-key="orderInfo.orderKey"
                    :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                    :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                    :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                    :is-editable="!props.disabled && (incompleteStatuses.includes(orderInfo.status) || isReopening)"
                    :form-button-clicked="formButtonClicked"
                    :llm-prediction="orderInfo.diagnosisLlmScores"
                    @update-primary-diagnosis="updatePrimaryDiagnosis"
                    @update-other-diagnosis="updateOtherDiagnosis"
                    @add-other-diagnosis="addOtherDiagnosis" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Additional Questions Section -->
      <v-expansion-panel
        v-if="orderInfo && orderInfo.basicPlusQuestions && orderInfo.basicPlusQuestions.length > 0"
        value="additional-questions">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-help-circle" class="mr-2"></v-icon>
          {{ basicPlusQuestionsTitle }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <QuestionsSelectionComponent
            :order-key="orderInfo.orderKey"
            :title="basicPlusQuestionsTitle"
            :is-editable="!props.disabled && (incompleteStatuses.includes(orderInfo.status) || isReopening)"
            :payer-questions="orderInfo.basicPlusQuestions"
            :checklist="orderInfo.basicPlusChecklist"
            :llm-prediction="orderInfo.basicPlusChecklistLlmScores"
            :form-button-clicked="formButtonClicked"
            :submission-method="orderInfo.submissionMethod"
            @update-checklist="updateBasicPlusChecklistSelection" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Insurance Coverage Criteria Section -->
      <v-expansion-panel
        v-if="
          orderInfo &&
          orderInfo.payerQuestions &&
          orderInfo.payerQuestions.length > 0 &&
          !orderInfo.editableSubmissionForm
        "
        value="insurance-coverage">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-shield-check" class="mr-2"></v-icon>
          {{ payerQuestionsTitle }}
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <QuestionsSelectionComponent
            :order-key="orderInfo.orderKey"
            :title="payerQuestionsTitle"
            :payer-questions="orderInfo.payerQuestions"
            :portal-payer-questions-json="orderInfo.portalPayerQuestionsJson"
            :is-editable="!props.disabled && (reviewStatuses.includes(orderInfo.status) || isReopening)"
            :checklist="orderInfo.checklist"
            :llm-prediction="orderInfo.checklistLlmScores"
            :policy-reviews="orderInfo.checklistPolicyReviews"
            :form-button-clicked="formButtonClicked"
            :submission-method="orderInfo.submissionMethod"
            @update-checklist="updateChecklistSelection" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Editable PDF Section -->
      <v-expansion-panel v-if="orderInfo && orderInfo.editableSubmissionForm" value="editable-pdf">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-file-pdf-box" class="mr-2"></v-icon>
          Submission Form
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <EditablePdfComponent
            v-if="!props.disabled || !props.filledFormPdfBlob"
            ref="editablePdf"
            :form-button-clicked="formButtonClicked"
            :pdf-form="orderInfo.editableSubmissionForm"
            :disabled="props.disabled" />
          <FlattenedPdfComponent v-else :pdf-form="props.filledFormPdfBlob" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Supporting Documents Section -->
      <v-expansion-panel value="supporting-docs">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-file-document" class="mr-2"></v-icon>
          Supporting Documents
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SupportingDocumentsPreviewComponent
            :order-key="orderInfo.orderKey"
            :medical-necessity="orderInfo.medicalNecessity"
            :patient-chart="orderInfo.patientChart"
            :medical-necessity-template="orderInfo.medicalNecessityTemplate"
            :supplemental-docs="supplementalDocs"
            :form-button-clicked="formButtonClicked"
            :is-editable="
              !props.disabled &&
              (incompleteStatuses.includes(orderInfo.status) ||
                reviewStatuses.includes(orderInfo.status) ||
                isReopening)
            "
            @add-uploaded-file="updateSupportingDocs"
            @delete-uploaded-file="updateSupportingDocs"
            @add-free-form-supplemental-docs="addFreeFormSupplementalDocs" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Expedited Review Section -->
      <v-expansion-panel value="expedited-review">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-clock-fast" class="mr-2"></v-icon>
          Expedited Review
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ExpeditedReviewComponent
            :expedited-review-within24h="orderInfo.expeditedReviewWithin24h"
            :expedited-review-statement="orderInfo.expeditedReviewStatement"
            :is-editable="!props.disabled && (incompleteStatuses.includes(orderInfo.status) || isReopening)"
            @update-order-info-field="updateOrderInfoField" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Signature Section -->
      <v-expansion-panel value="signature">
        <v-expansion-panel-title class="text-primary weight-600">
          <v-icon icon="mdi-pen" class="mr-2"></v-icon>
          Signature
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SignatureInputComponent
            :order-key="orderInfo.orderKey"
            :signature="orderInfo.signature"
            :form-button-clicked="formButtonClicked"
            :disabled="props.disabled"
            @update-signature="updateSignature" />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-row v-if="!preloader" class="mt-4 justify-end">
      <v-col cols="auto">
        <v-btn variant="outlined" :disabled="draftSavingInProgress" @click="sendToPrescriber">
          Send to Prescriber
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          variant="outlined"
          :disabled="draftSavingInProgress || props.disabled"
          :loading="draftSavingInProgress"
          @click="handleSavePartialOrderInfo">
          Save Draft
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          :disabled="draftSavingInProgress || props.disabled || (isReopening && !isReopenEligible)"
          @click="submitOrderInfo">
          {{ buttonText }}
        </v-btn>
      </v-col>
    </v-row>
    <span v-if="errors.length > 0" class="input-error-notification">
      <span v-for="error in errors" :key="error">{{ error }} </span>
    </span>

    <!-- Policy Warning Confirmation Dialog -->
    <v-dialog v-model="showPolicyWarningDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Policy Compliance Warning
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            {{ policyIssueCount }} answer{{ policyIssueCount > 1 ? "s are" : " is" }} unlikely to be approved based on
            the payer's coverage criteria.
          </p>
          <p class="text-body-2 text-medium-emphasis">Are you sure you want to submit this request?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="showPolicyWarningDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmSubmitWithPolicyWarning">Submit Anyway</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";
import { tryParseApiErrors, clearOrderInfoStorage } from "@/utils";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useFieldChangeTracker } from "@/composables/useFieldChangeTracker";
import { useOrderInfoUpdates } from "@/composables/useOrderInfoUpdates";
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview.vue";
import ProviderInformationFormPreview from "@/pages/ProviderInformationFormPreview.vue";
import MedicalInformationForm from "@/pages/MedicalInformationForm.vue";
import QuestionsSelectionComponent from "@/components/QuestionsSelectionComponent.vue";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import ExpeditedReviewComponent from "@/components/ExpeditedReviewComponent.vue";
import SignatureInputComponent from "@/components/SignatureInputComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import EditablePdfComponent from "@/components/EditablePdfComponent.vue";
import PortalFormSelection from "@/components/PortalFormSelection.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import FlattenedPdfComponent from "@/components/FlattenedPdfComponent.vue";
// import PharmacyLookupComponent from "@/components/PharmacyLookupComponent";

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  filledFormPdfBlob: {
    type: Blob,
    default: null,
  },
});

const emit = defineEmits(["sendToPrescriber", "sendToPlan"]);

const { orderInfo } = storeToRefs(useOrderInfoStore());

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info", "Requires review"];
const reviewStatuses = ["Review clinical info", "Requires review"];
const reopeningStatuses = ["Payor/Medication not supported", "Response received"];
const isReopening = computed(() => {
  return reopeningStatuses.includes(orderInfo.value.status) && orderInfo.value.submissionMethod === "CoverMyMeds";
});
const buttonText = computed(() => {
  if (isReopening.value) return "Re-Open Submission";

  if (incompleteStatuses.includes(orderInfo.value.status) && orderInfo.value.status !== "Requires review") {
    return "Send to Processing";
  } else {
    return "Send to Plan";
  }
});

const { trackChange, isReopenEligible, requiresNewSubmission } = useFieldChangeTracker(orderInfo.value);

const {
  supplementalDocs,
  formButtonClicked,
  updateChecklistSelection,
  updateBasicPlusChecklistSelection,
  updateSignature,
  updatePrimaryDiagnosis,
  updateOtherDiagnosis,
  addOtherDiagnosis,
  addFreeFormSupplementalDocs,
  updateSupportingDocs,
  addExtraMedication,
  removeExtraMedication,
  removePrimaryMedication,
  updateExtraMedicationField,
  validPatientInfo,
  validProviderInfo,
  validMedicationInfo,
  validDiagnosisInfo,
  validSupportingDocs,
  mergeSupplementalDocsWithPatientChart,
  savePartialOrderInfo,
} = useOrderInfoUpdates(orderInfo);

const expandedSections = ref([]);
const showPolicyWarningDialog = ref(false);

const draftSavingInProgress = ref(false);

const policyIssueCount = computed(() => {
  const reviews = orderInfo.value.checklistPolicyReviews || [];
  return reviews.filter((r) => r.alignsWithPolicy === "NO").length;
});
const errors = ref([]);

const router = useRouter();
const preloader = ref(false);
const medicationSearchBox = ref([]);
const editablePdf = ref(null);
const payerQuestionsTitle = "Insurance Coverage Criteria";
const basicPlusQuestionsTitle = "Additional Questions";

onMounted(() => {
  orderInfo.value.formStep = "Review";
  supplementalDocs.value.push({ label: null, links: [] });
  getMedicationSearchBox();
  setDefaultExpandedSections();
});

onUnmounted(() => {
  expandedSections.value = [];
  clearOrderInfoStorage();
});

async function getMedicationSearchBox() {
  preloader.value = true;
  medicationSearchBox.value = await mainServices.getMedicationSearchBox();
  preloader.value = false;
}

function updateOrderInfoField(field, value) {
  orderInfo.value[field] = value;
  trackChange(field, value);
}

function setDefaultExpandedSections() {
  if (incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value = [
      "patient-info",
      "provider-info",
      "medical-info",
      "diagnosis-info",
      "supporting-docs",
      "expedited-review",
      "signature",
    ];

    if (orderInfo.value.basicPlusChecklist.length > 0) {
      expandedSections.value.push("additional-questions");
    }
    if (orderInfo.value.payerQuestions.length > 0 && !orderInfo.value.editableSubmissionForm) {
      expandedSections.value.push("insurance-coverage");
    }
    if (orderInfo.value.editableSubmissionForm) {
      expandedSections.value.push("editable-pdf");
    }
  }

  if (reviewStatuses.includes(orderInfo.value.status)) {
    expandedSections.value = ["insurance-coverage", "editable-pdf", "signature"];
  }
}

async function handleSavePartialOrderInfo() {
  draftSavingInProgress.value = true;
  if (orderInfo.value.editableSubmissionForm) {
    if (!expandedSections.value.includes("editable-pdf")) {
      expandedSections.value.push("editable-pdf");
    }
    if (!editablePdf.value) {
      // timeout to ensure the component is mounted before validation
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (editablePdf.value) {
      editablePdf.value.mergeOverlayToPdfForm();
    }
  }
  try {
    errors.value = [];
    await savePartialOrderInfo();
  } catch (error) {
    errors.value = ["Failed to save draft. Please try again."];
  }
  draftSavingInProgress.value = false;
}

defineExpose({
  saveDraft: handleSavePartialOrderInfo,
  performSubmission,
});

async function validateForm(shouldCheckPolicy = true) {
  formButtonClicked.value = true;
  if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
    mergeSupplementalDocsWithPatientChart();
  }
  errors.value = [];
  if (!validPatientInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("patient-info");
    errors.value.push("Please fill in all required patient information fields.");
  }
  if (!validProviderInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("provider-info");
    errors.value.push("Please fill in all required provider information fields.");
  }
  if (!validMedicationInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("medical-info");
    errors.value.push("Please fill in all required medication information fields.");
  }
  if (!validDiagnosisInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("diagnosis-info");
    errors.value.push("Please fill in all required diagnosis information fields.");
  }
  if (!validSupportingDocs()) {
    expandedSections.value.push("supporting-docs");
    errors.value.push("Please add at least one supporting document.");
  }
  if (orderInfo.value.editableSubmissionForm) {
    if (!expandedSections.value.includes("editable-pdf")) {
      expandedSections.value.push("editable-pdf");
    }
    if (!editablePdf.value) {
      // timeout to ensure the component is mounted before validation
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (editablePdf.value) {
      editablePdf.value.mergeOverlayToPdfForm();
      const isValid = editablePdf.value.validateAll();
      if (!isValid) {
        errors.value.push("Please fill in all required fields in the PDF form.");
      }
    }
  }
  expandedSections.value.push("insurance-coverage");
  expandedSections.value.push("additional-questions");
  await new Promise((resolve) => setTimeout(resolve, 300));
  const errorClassNames = ["v-input--error", "form-validation-alert"];
  let missingInput = [];
  errorClassNames.forEach((className) => {
    const foundElements = document.getElementsByClassName(className);
    missingInput = [...missingInput, ...foundElements];
  });
  if (missingInput.length === 0) {
    // Check for policy compliance issues before submitting
    if (shouldCheckPolicy && policyIssueCount.value > 0) {
      showPolicyWarningDialog.value = true;
      return false;
    }
    return true;
  } else {
    setTimeout(() => {
      const parentDiv = missingInput[0].parentNode;
      window.scrollTo({
        top: parentDiv.offsetTop,
        behavior: "smooth",
      });
    }, 0);
    return false;
  }
}

async function submitOrderInfo() {
  const isValid = await validateForm(true);
  if (!isValid) {
    return;
  }
  // Emit sendToPlan event - parent can intercept for eFax submissions
  emit("sendToPlan");
  // Only proceed with submission if not intercepted (handled by parent)
  // For eFax, parent will show fax editor instead
  if (orderInfo.value?.submissionMethod !== "eFax") {
    await performSubmission();
  }
}

async function confirmSubmitWithPolicyWarning() {
  showPolicyWarningDialog.value = false;
  emit("sendToPlan");
  // Only proceed with submission if not intercepted (handled by parent)
  if (orderInfo.value?.submissionMethod !== "eFax") {
    await performSubmission();
  }
}

async function performSubmission() {
  draftSavingInProgress.value = true;
  try {
    if (isReopening.value) {
      if (!isReopenEligible.value) {
        draftSavingInProgress.value = false;
        return;
      }
      // Include field change tracking data for backend processing
      const submissionData = {
        ...orderInfo.value,
        requiresNewSubmission: requiresNewSubmission.value,
      };
      await orderInfoService.reopenSubmission(orderInfo.value.orderKey, submissionData);
      errors.value = [];
      expandedSections.value = [];
      clearOrderInfoStorage();
      router.push({ name: "all-submissions" });
      window.scrollTo(0, 0);
    } else {
      orderInfo.value.formStep = "Send to Plan";
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }
      if (orderInfo.value.editableSubmissionForm) {
        if (!expandedSections.value.includes("editable-pdf")) {
          expandedSections.value.push("editable-pdf");
        }
        if (!editablePdf.value) {
          // timeout to ensure the component is mounted before validation
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        if (editablePdf.value) {
          editablePdf.value.mergeOverlayToPdfForm();
        }
      }
      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value);
      errors.value = [];
      expandedSections.value = [];
      clearOrderInfoStorage();
      router.push({ name: "all-submissions" });
      window.scrollTo(0, 0);
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  draftSavingInProgress.value = false;
}

async function changeFormSelection(selectedFormId) {
  await new Promise((resolve) => setTimeout(resolve, 0));
  preloader.value = true;
  try {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    let data = orderInfo.value;
    data.portalFormId = selectedFormId;
    await orderInfoService.changeFormSelection(orderInfo.value.orderKey, data);
    errors.value = [];
    expandedSections.value = [];
    clearOrderInfoStorage();
    router.push({ name: "all-submissions" });
    window.scrollTo(0, 0);
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

async function changeFormulary() {
  await new Promise((resolve) => setTimeout(resolve, 0));
  preloader.value = true;
  try {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    await orderInfoService.changeOrderFormulary(orderInfo.value.orderKey, orderInfo.value);
    errors.value = [];
    expandedSections.value = [];
    clearOrderInfoStorage();
    router.push({ name: "all-submissions" });
    window.scrollTo(0, 0);
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

async function sendToPrescriber() {
  // Only validate if submission is in editable status
  if (incompleteStatuses.includes(orderInfo.value.status) || reviewStatuses.includes(orderInfo.value.status)) {
    const isValid = await validateForm(false);
    if (!isValid) {
      return;
    }
  }
  emit("sendToPrescriber");
}
</script>

<style lang="scss" scoped></style>
